import { computed, makeObservable, observable } from 'mobx';
import merge from 'lodash.merge';

import { entityPool, Model } from '../../core/engine/';
import { ManyToMany, ManyToOne, OneToMany } from '../../core/engine/decorators';
import {
  Space,
  Shot,
  Character,
  Location,
  Stage,
  Asset,
  Storyboard,
  Contact,
  Step,
  Task,
  Team,
  ShareLink,
  Stripboard,
  Member,
} from './';
import { Address } from '../../dto/common/address.dto';
import { TeamContact } from '../../features/teams/models/team';
import { api } from '../../api';
import { upload } from '../../core/services/file.service';
import { Frame } from '../../lib/utils/transform-image';
import { ProjectTasksFilter } from './filters/project-tasks-filter';
import { Property } from './property';
import { View } from './view';

export const ProjectPriorityByValue = ['No Priority', 'Low', 'Medium', 'High', 'Urgent'];

export enum ProjectPriority {
  NO_PRIORITY = 0,
  LOW = 1,
  MEDIUM = 2,
  HIGH = 3,
  URGENT = 4,
}

export class Project extends Model {
  /** The project's name **/
  name = '';

  /** The project' cover image **/
  avatar: string;

  spaceId = '';

  contacts: TeamContact[] = [];

  dueDate: Date;

  startDate: Date;

  /** The project's collaborator **/
  @ManyToMany('projects')
  collaborators: Member[] = [];

  collaboratorsIds: string[];

  /** The project's collaborator **/
  @OneToMany('project')
  characters: Character[] = [];

  /** The project's properties **/
  @OneToMany('project')
  properties: Property[] = [];

  /** The project's shots **/
  @OneToMany('project')
  shots: Shot[] = [];

  /** The project's locations **/
  @OneToMany('project')
  locations: Location[] = [];

  /** The project's stages **/
  @OneToMany('project')
  stages: Stage[] = [];

  /** The project's steps **/
  @OneToMany('project')
  steps: Step[] = [];

  /** The project's assets **/
  @OneToMany('project')
  assets: Asset[] = [];

  /** The project's teams **/
  @OneToMany('project')
  teams: Team[] = [];

  /** The project's share links **/
  @OneToMany('project')
  shareLinks: ShareLink[] = [];

  @OneToMany('project')
  tasks: Task[] = [];

  @OneToMany('project')
  views: View[] = [];

  taskCount?: number;

  // Is project fully duplicated?
  finishedCopying?: boolean;

  stripboard: Stripboard;

  /** The project's space **/
  @ManyToOne('projects')
  space: Space | null = null;

  client?: {
    name?: string;

    logo?: {
      src?: string;
    };

    address?: Address;
  };

  agency?: {
    name?: string;

    logo?: {
      src?: string;
    };

    address?: Address;
  };

  production?: {
    name?: string;

    phone?: string;

    logo?: {
      src?: string;
    };

    address?: Address;
  };

  aspectRatio?: string;
  removeTimeStamp?: string;
  currentStepId: string;
  src: string;
  directors: string[];
  producers: string[];
  lineProducers: string[];
  currentTask: string;
  lastShotIndex: number;
  status?: string;
  lastUpdatedAt?: string;
  usedStorage?: number;

  priority?: ProjectPriority;

  rootAssetId: string;
  documentsFolderId: string;

  tasksFilter: ProjectTasksFilter;

  // Archive fields
  archived?: boolean;
  archivedAt?: Date;
  archivedById?: string;

  // Creator field (for permission checks)
  createdBy?: string;

  private _fps?: number;

  set fps(fps: number) {
    this._fps = fps;
  }

  get fps(): number {
    return this._fps ? this._fps : this.space!.fps;
  }

  private _progress = 0;

  set progress(progress: number) {
    this._progress = Math.round(progress);
  }

  get progress(): number {
    return Math.round(this._progress);
  }

  @OneToMany('project')
  storyboards: Storyboard[] = [];

  /*******************
   * Computed values *
   *******************/
  get shootingDays(): Step[] {
    return this.steps.filter((step) => step.type === 'shootingDay');
  }

  set shootingDays(_: string) {}

  get storyboard(): Storyboard | null {
    const storyboard = this.storyboards[0];

    if (!storyboard) {
      return null;
    }

    return storyboard;
  }

  constructor() {
    super('projects');
    this.shots = [];
    this.storyboards = [];
    this.contacts = [];
    this.collaborators = [];
    this.characters = [];
    this.shots = [];
    this.locations = [];
    this.stages = [];
    this.steps = [];
    this.assets = [];
    this.teams = [];
    this.shareLinks = [];
    this.tasks = [];
    this.properties = [];
    this.usedStorage = 0;
    this.priority = ProjectPriority.NO_PRIORITY;
    this.tasksFilter = new ProjectTasksFilter();

    makeObservable(this, {
      collaborators: observable,
      characters: observable,
      locations: observable,
      tasks: observable,
      shots: observable,
      stages: observable,
      steps: observable,
      contacts: observable,
      name: observable,
      avatar: observable,
      spaceId: observable,
      dueDate: observable,
      startDate: observable,
      assets: observable,
      teams: observable,
      shareLinks: observable,
      space: observable,
      client: observable,
      agency: observable,
      production: observable,
      aspectRatio: observable,
      removeTimeStamp: observable,
      properties: observable,
      currentStepId: observable,
      src: observable,
      directors: observable,
      producers: observable,
      lineProducers: observable,
      currentTask: observable,
      lastShotIndex: observable,
      status: observable,
      lastUpdatedAt: observable,
      usedStorage: observable,
      priority: observable,
      tasksFilter: observable,
      storyboards: observable,
      finishedCopying: observable,
      archived: observable,
      archivedAt: observable,
      archivedById: observable,
      storyboard: computed,
      shootingDays: computed,
      fps: computed,
      progress: computed,
    });
  }

  async addKeyContact(contact: Contact, role: string) {
    await api.post(`/projects/${this._id}/contacts`, {
      contactId: contact._id,
      role,
    });

    this.contacts.push({
      ...contact,
      phone: contact.phone || '',
      _id: contact._id,
      role,
    });
  }

  async removeKeyContact(contactId: string) {
    await api.delete(`/projects/${this._id}/contacts/${contactId}`);
    this.contacts = this.contacts.filter((item) => item._id !== contactId);
  }

  override async delete() {
    super.delete();

    this.tasks?.forEach((task) => entityPool.delete(task));
  }

  override async update(values: Record<string, any>): Promise<void> {
    const files: any = {};

    const filterFiles = (values: any) => {
      const filters = ['production', 'client', 'agency'];

      filters.forEach((filter) => {
        if (values[filter] && values[filter].logo && values[filter].logo.file) {
          files[filter] = values[filter].logo.file;
          delete values[filter].logo.file;
        }
      });

      return values;
    };

    const { data } = await api.patch(`/projects/${this._id}`, filterFiles(values));

    if (data.links.uploads) {
      await Promise.all(
        Object.keys(data.links.uploads).map((key) => {
          // @ts-ignore
          const company = values[key];
          const url = data.links.uploads[key];

          if (!url || !company || !company.logo) {
            return;
          }

          const file = files[key];

          upload(url, file);
        }),
      );
    }

    if (values.fps !== undefined) {
      this.shots.forEach((el) => {
        el.fps = values.fps;
      });
    }

    merge(this, data);
  }

  static override getOne(id: string): Project | undefined {
    return Model.getOne(id) as Project;
  }

  async updateCover(file: Frame) {
    const { data } = await api.put(`/projects/${this._id}/cover`, {
      fileSize: file.size,
      fileType: file.type,
    });

    // @ts-ignore
    await upload(data.links.upload, file.data ? file.data : file);

    merge(this, data);
  }

  canAddShot() {
    return this.space?.hasSubscription() || this.shots.length < 10;
  }

  async addCollaborator(memberId: string) {
    if (this.collaboratorsIds.includes(memberId)) {
      return;
    }

    const member = Member.getOne(memberId);
    if (!member) return;

    this.collaboratorsIds.push(memberId);
    this.collaborators = [...this.collaborators, member];

    await api.post(`/projects/${this._id}/collaborators`, { memberId });
  }

  async removeCollaborator(collaboratorId: string) {
    if (!this.collaboratorsIds.includes(collaboratorId)) {
      return;
    }

    this.collaboratorsIds = this.collaboratorsIds.filter((id) => id !== collaboratorId);
    this.collaborators = this.collaborators.filter(
      (collaborator) => collaborator._id !== collaboratorId,
    );

    await api.delete(`/projects/${this._id}/collaborators/${collaboratorId}`);
  }

  toPOJO(): Record<string, any> {
    const pojo = {
      _id: this._id,
      name: this.name,
      avatar: this.avatar,
      progress: this.progress,
      spaceId: this.spaceId,
      dueDate: this.dueDate,
      startDate: this.startDate,
      contacts: this.contacts,
      collaborators: this.collaborators,
      characters: this.characters,
      locations: this.locations,
      shots: this.shots,
      stages: this.stages,
      steps: this.steps,
      assets: this.assets,
      space: this.space,
      client: this.client,
      agency: this.agency,
      production: this.production,
      aspectRatio: this.aspectRatio,
      removeTimeStamp: this.removeTimeStamp,
      currentStepId: this.currentStepId,
      src: this.src,
      directors: this.directors,
      producers: this.producers,
      lineProducers: this.lineProducers,
      currentTask: this.currentTask,
      lastShotIndex: this.lastShotIndex,
      status: this.status,
      fps: this.fps,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };

    // if a value is undefined, we remove it from the POJO
    Object.keys(pojo).forEach((key) => pojo[key] === undefined && delete pojo[key]);
    return pojo;
  }

  static PriorityMapper: Record<ProjectPriority, { title: string }> = {
    [ProjectPriority.NO_PRIORITY]: {
      title: 'No Priority',
    },
    [ProjectPriority.LOW]: {
      title: 'Low',
    },
    [ProjectPriority.MEDIUM]: {
      title: 'Medium',
    },
    [ProjectPriority.HIGH]: {
      title: 'High',
    },
    [ProjectPriority.URGENT]: {
      title: 'Urgent',
    },
  };

  static PriorityArray = Object.entries(Project.PriorityMapper)
    .slice()
    .sort((a, b) => Number(b[0]) - Number(a[0]));
}
